import { useState, useEffect } from 'react';
import {
  Upload,
  Play,
  Pause,
  XCircle,
  Trash2,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { ImportJob, ImportJobLog } from '../types';
import { formatDateTime, getStatusLabel } from '../utils/format';
import { api } from '../api/client';

export default function DataImport() {
  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  const [jobLogs, setJobLogs] = useState<Record<string, ImportJobLog[]>>({});
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    job_type: 'historical_import' as ImportJob['job_type'],
    start_date: '',
    end_date: '',
    batch_size: 10,
    delay_between_batches_ms: 5000,
  });

  const loadJobs = async () => {
    try {
      const response = await api.getImportJobs();
      setJobs(response.jobs || []);
    } catch (err) {
      console.error('Failed to load import jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobLogs = async (jobId: string) => {
    try {
      const response = await api.getImportJobLogs(jobId);
      setJobLogs((prev) => ({ ...prev, [jobId]: response.logs || [] }));
    } catch (err) {
      console.error('Failed to load job logs:', err);
    }
  };

  const handleExpandJob = (jobId: string) => {
    if (expandedJob === jobId) {
      setExpandedJob(null);
    } else {
      setExpandedJob(jobId);
      if (!jobLogs[jobId]) {
        loadJobLogs(jobId);
      }
    }
  };

  const handleCreateJob = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionLoading('create');
    try {
      await api.createImportJob({
        name: formData.name,
        job_type: formData.job_type,
        start_date: formData.start_date || undefined,
        end_date: formData.end_date || undefined,
        batch_size: formData.batch_size,
        delay_between_batches_ms: formData.delay_between_batches_ms,
      });
      setShowCreateForm(false);
      setFormData({
        name: '',
        job_type: 'historical_import',
        start_date: '',
        end_date: '',
        batch_size: 10,
        delay_between_batches_ms: 5000,
      });
      await loadJobs();
    } catch (err) {
      console.error('Failed to create job:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleJobAction = async (jobId: string, action: string) => {
    setActionLoading(`${action}-${jobId}`);
    try {
      switch (action) {
        case 'start':
          await api.startImportJob(jobId);
          break;
        case 'pause':
          await api.pauseImportJob(jobId);
          break;
        case 'cancel':
          await api.cancelImportJob(jobId);
          break;
        case 'continue':
          await api.continueImportJob(jobId);
          break;
        case 'delete':
          await api.deleteImportJob(jobId);
          break;
      }
      await loadJobs();
    } catch (err) {
      console.error(`Failed to ${action} job:`, err);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const classes: Record<string, string> = {
      pending: 'badge-gray',
      running: 'badge-blue',
      paused: 'badge-yellow',
      completed: 'badge-green',
      failed: 'badge-red',
      cancelled: 'badge-gray',
    };
    return classes[status] || 'badge-gray';
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-500">Lade Import Jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Datenimport</h2>
          <p className="text-sm text-gray-500">
            Importiere historische Daten von Aircall
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={loadJobs} className="btn-secondary" disabled={loading}>
            <RefreshCw className={clsx('w-4 h-4 mr-2', loading && 'animate-spin')} />
            Aktualisieren
          </button>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="btn-primary"
          >
            <Upload className="w-4 h-4 mr-2" />
            Neuer Import
          </button>
        </div>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="card p-6 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Neuen Import Job erstellen
          </h3>
          <form onSubmit={handleCreateJob} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="z.B. Januar 2025 Import"
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job Typ
                </label>
                <select
                  value={formData.job_type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      job_type: e.target.value as ImportJob['job_type'],
                    })
                  }
                  className="select"
                >
                  <option value="historical_import">Historischer Import</option>
                  <option value="bulk_transcribe">Bulk Transkription</option>
                  <option value="bulk_evaluate">Bulk Bewertung</option>
                  <option value="bulk_sync">Bulk Sync</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Von Datum
                </label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) =>
                    setFormData({ ...formData, start_date: e.target.value })
                  }
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bis Datum
                </label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) =>
                    setFormData({ ...formData, end_date: e.target.value })
                  }
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Batch Size
                </label>
                <input
                  type="number"
                  value={formData.batch_size}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      batch_size: parseInt(e.target.value) || 10,
                    })
                  }
                  min={1}
                  max={50}
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Delay zwischen Batches (ms)
                </label>
                <input
                  type="number"
                  value={formData.delay_between_batches_ms}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      delay_between_batches_ms: parseInt(e.target.value) || 5000,
                    })
                  }
                  min={1000}
                  step={1000}
                  className="input"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="btn-secondary"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={actionLoading === 'create'}
                className="btn-primary"
              >
                {actionLoading === 'create' ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Job erstellen
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Jobs List */}
      {jobs.length === 0 ? (
        <div className="card p-8 text-center">
          <Upload className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Keine Import Jobs
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Erstelle einen neuen Import Job um historische Daten zu importieren.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => (
            <div key={job.id} className="card overflow-hidden">
              {/* Job Header */}
              <div
                className="px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                onClick={() => handleExpandJob(job.id)}
              >
                <div className="flex items-center space-x-4">
                  {expandedJob === job.id ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">
                      {job.name}
                    </h3>
                    <p className="text-xs text-gray-500">
                      Erstellt: {formatDateTime(job.created_at)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  {/* Progress */}
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {job.processed_calls} / {job.total_calls || '?'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {job.error_count > 0 && (
                        <span className="text-red-500">
                          {job.error_count} Fehler
                        </span>
                      )}
                    </p>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-32">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={clsx('h-2 rounded-full', {
                          'bg-green-500': job.status === 'completed',
                          'bg-blue-500': job.status === 'running',
                          'bg-yellow-500': job.status === 'paused',
                          'bg-red-500': job.status === 'failed',
                          'bg-gray-400':
                            job.status === 'pending' || job.status === 'cancelled',
                        })}
                        style={{
                          width: `${
                            job.total_calls
                              ? Math.round((job.processed_calls / job.total_calls) * 100)
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                  </div>

                  {/* Status Badge */}
                  <span className={clsx('badge', getStatusBadge(job.status))}>
                    {getStatusLabel(job.status)}
                  </span>

                  {/* Actions */}
                  <div
                    className="flex items-center space-x-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {job.status === 'pending' && (
                      <button
                        onClick={() => handleJobAction(job.id, 'start')}
                        disabled={actionLoading !== null}
                        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                        title="Starten"
                      >
                        <Play className="w-4 h-4 text-green-600" />
                      </button>
                    )}
                    {job.status === 'running' && (
                      <button
                        onClick={() => handleJobAction(job.id, 'pause')}
                        disabled={actionLoading !== null}
                        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                        title="Pausieren"
                      >
                        <Pause className="w-4 h-4 text-yellow-600" />
                      </button>
                    )}
                    {job.status === 'paused' && (
                      <button
                        onClick={() => handleJobAction(job.id, 'continue')}
                        disabled={actionLoading !== null}
                        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                        title="Fortsetzen"
                      >
                        <Play className="w-4 h-4 text-green-600" />
                      </button>
                    )}
                    {(job.status === 'running' || job.status === 'paused') && (
                      <button
                        onClick={() => handleJobAction(job.id, 'cancel')}
                        disabled={actionLoading !== null}
                        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                        title="Abbrechen"
                      >
                        <XCircle className="w-4 h-4 text-red-600" />
                      </button>
                    )}
                    {(job.status === 'completed' ||
                      job.status === 'failed' ||
                      job.status === 'cancelled') && (
                      <button
                        onClick={() => handleJobAction(job.id, 'delete')}
                        disabled={actionLoading !== null}
                        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                        title="Loschen"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedJob === job.id && (
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                  {/* Job Details */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                    <div>
                      <p className="text-gray-500">Typ</p>
                      <p className="font-medium">{job.job_type}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Batch Size</p>
                      <p className="font-medium">{job.batch_size}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Delay</p>
                      <p className="font-medium">{job.delay_between_batches_ms}ms</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Zeitraum</p>
                      <p className="font-medium">
                        {job.start_date && job.end_date
                          ? `${formatDateTime(job.start_date)} - ${formatDateTime(job.end_date)}`
                          : '-'}
                      </p>
                    </div>
                  </div>

                  {/* Logs */}
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Letzte Logs
                  </h4>
                  <div className="bg-white rounded border border-gray-200 max-h-48 overflow-y-auto">
                    {!jobLogs[job.id] ? (
                      <div className="p-4 text-center text-gray-500">
                        <RefreshCw className="w-4 h-4 animate-spin mx-auto" />
                      </div>
                    ) : jobLogs[job.id].length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        Keine Logs vorhanden
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {jobLogs[job.id].slice(0, 10).map((log) => (
                          <div
                            key={log.id}
                            className="px-4 py-2 flex items-start space-x-3"
                          >
                            {log.status === 'success' ? (
                              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                            ) : log.status === 'error' ? (
                              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                            ) : (
                              <Pause className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-900 truncate">
                                {log.message || log.status}
                              </p>
                              {log.error_details && (
                                <p className="text-xs text-red-500 truncate">
                                  {log.error_details}
                                </p>
                              )}
                              <p className="text-xs text-gray-400">
                                {formatDateTime(log.created_at)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
